import type {
    ResponseNotifyParams,
    ResponseNotifyResult
} from "../../types/api";

/**
 * Returns only a resolved response success status
 * or null if not available.
 *
 * Useful for conditional UI logic.
 */
export const getResponseNotifyIsSuccess = (
    params: ResponseNotifyParams
): boolean | null => {
    const data = getResponseNotifyData(params);
    return data?.success ?? null;
};

/**
 * Returns only a resolved UI message (string)
 * or null if nothing can be resolved.
 *
 * Intended for simple toast / alert usage.
 */
export const getResponseNotifyMessage = (
    params: ResponseNotifyParams
): string | null => {
    const data = getResponseNotifyData(params);
    return data?.message ?? null;
};

/**
 * Returns only a resolved response code
 * or null if not available.
 *
 * Useful for conditional UI logic.
 */
export const getResponseNotifyCode = (
    params: ResponseNotifyParams
): number | null => {
    const data = getResponseNotifyData(params);
    return data?.code ?? null;
};

/**
 * Resolves a UI-friendly notification object
 * from API response or thrown error.
 *
 * This function is the SINGLE source of truth
 * for response-related messages across the app.
 *
 * Resolution priority:
 * 1. Backend response (success or controlled error)
 * 2. Thrown error (Axios / fetch / runtime)
 * 3. Fallback message
 */
export const getResponseNotifyData = (
    params: ResponseNotifyParams
): ResponseNotifyResult => {
    const {
        response,
        error,
        successFallback = "Uspešno.",
        errorFallback = "Greška.",
    } = params;

    /**
     * Backend-controlled API response
     */
    if (response) {
        // SUCCESS
        if (response?.success === true) {
            return {
                success: true,
                message: getSuccessMessage(response, successFallback),
                code: response.code
            };
        }

        // FAILURE (backend handled error)
        if (response?.success === false) {

            // TODO: Napraviti proveru da li je u pitanju validacija forme i na osnovu toga napraviti kompletan prikaz greske

            return {
                success: false,
                message: getErrorMessage(response, errorFallback),
                code: response.code
            };
        }
    }

    /**
     * Thrown error (Axios / fetch / runtime)
     */
    if (error) {
        return {
            success: false,
            message: getErrorMessage(error, errorFallback),
            code: getErrorCode(error)
        };
    }

    /**
     * Absolute fallback (should rarely happen)
     */
    return {
        success: false,
        message: errorFallback,
    };
};

/**
 * Extracts a human-readable error message
 * from different error shapes.
 *
 * Supported cases:
 * - Backend response object (ApiResponse)
 * - Axios error: error.response.data.message
 * - Axios validation errors: error.response.data.errors[]
 * - Native JS Error: error.message
 */
export const getErrorMessage = (
    error: unknown,
    fallback: string = "Greška."
): string => {
    if (!error) return fallback;

    /**
     * ApiResponse or object response
     */
    if (typeof error === "object" && error !== null) {
        const anyResponse = (error as any).response;

        if (typeof anyResponse.data?.payload?.message === "string") {
            return anyResponse.data?.payload.message;
        }

        if (typeof anyResponse.data?.message === "string") {
            return anyResponse.data?.message;
        }
    }

    /**
     * Backend response object (ApiResponse)
     */
    if (
        typeof error === "object" &&
        error !== null &&
        "message" in error
    ) {
        const anyError = error as any;
        if (typeof anyError.message === "string" && anyError.message.length) {
            return anyError.message;
        }
    }

    /**
     * Axios-style error
     */
    if (
        typeof error === "object" &&
        error !== null &&
        "response" in error
    ) {
        const response = (error as any).response;

        if (response?.data?.message) {
            return String(response.data.message);
        }

        if (Array.isArray(response?.data?.errors)) {
            return response.data.errors.join(", ");
        }
    }

    /**
     * Native JS Error / fetch error
     */
    if (error instanceof Error && error.message) {
        return error.message;
    }

    return fallback;
};


export const getErrorCode = (
    error: unknown,
    fallbackCode: number = 500
): number | null => {
    if (!error) return fallbackCode;

    /**
     * ApiResponse or object response
     */
    if (typeof error === "object" && error !== null) {
        const anyResponse = (error as any).response;

        if (typeof anyResponse?.data?.code === "number") {
            return anyResponse?.data?.code;
        }

        if (typeof anyResponse?.status === "number") {
            return anyResponse?.status;
        }
    }

    if (typeof error === "object" && error !== null) {
        const anyError = (error as any);

        if (typeof anyError?.status === "number") {
            return anyError?.status;
        }
    }

    return fallbackCode;
};

/**
 * Extracts a human-readable success message
 * from API responses.
 *
 * Supported cases:
 * - response.message
 * - response.payload.message
 * - String response
 */
export const getSuccessMessage = (
    response: unknown,
    fallback: string = "Uspešno."
): string => {
    if (!response) return fallback;

    /**
     * ApiResponse or object response
     */
    if (typeof response === "object" && response !== null) {
        const anyResponse = response as any;

        if (typeof anyResponse.message === "string") {
            return anyResponse.message;
        }

        if (typeof anyResponse.payload?.message === "string") {
            return anyResponse.payload.message;
        }
    }

    /**
     * Backend returned string directly
     */
    if (typeof response === "string") {
        return response;
    }

    return fallback;
};
